import type { BonusGoal } from '../level.js';
import type { GameState, Passenger } from '../types.js';

/**
 * A shift's bonus goal (ADR-0040): one thing beyond winning it, asked of how
 * the run was played rather than of the lift it was played on.
 *
 * It decides nothing about the run. It reads the state the run left behind,
 * changes no field the hash covers, and so no replay moves for it.
 */

/** Who has a deadline to meet: couriers, and commuters for their train. Never a crew. */
export function onTheClock(passenger: Passenger): boolean {
  return passenger.deadlineMs !== null && !passenger.critical;
}

/** Delivered, and before their deadline. */
function onTime(passenger: Passenger): boolean {
  return (
    passenger.state === 'delivered' &&
    passenger.deliveredMs !== null &&
    passenger.deadlineMs !== null &&
    passenger.deliveredMs <= passenger.deadlineMs
  );
}

/** Whom the goal is about in this shift, where it is about someone. */
export function bonusSubjects(state: GameState, bonus: BonusGoal): readonly Passenger[] {
  switch (bonus.kind) {
    case 'couriersOnTime':
      return state.passengers.filter(onTheClock);
    case 'allVips':
      return state.passengers.filter((p) => p.vip);
    case 'noneLost':
    case 'withinTrips':
      return state.passengers;
  }
}

export interface BonusStatus {
  /** Out of reach for the rest of this run: someone lost, a deadline missed, a trip too many. */
  readonly missed: boolean;
  /** Won, and the goal with it. */
  readonly met: boolean;
}

export function bonusStatus(state: GameState, bonus: BonusGoal): BonusStatus {
  const missed = bonusMissed(state, bonus);
  return { missed, met: !missed && state.status === 'won' && bonusDone(state, bonus) };
}

function bonusMissed(state: GameState, bonus: BonusGoal): boolean {
  switch (bonus.kind) {
    case 'noneLost':
      return state.passengers.some((p) => p.state === 'abandoned');
    case 'couriersOnTime':
      return bonusSubjects(state, bonus).some(
        (p) =>
          p.state === 'abandoned' ||
          (p.state === 'delivered' && !onTime(p)) ||
          (p.deadlineMs !== null && state.elapsedMs > p.deadlineMs && p.state !== 'delivered'),
      );
    case 'allVips':
      return bonusSubjects(state, bonus).some((p) => p.state === 'abandoned');
    case 'withinTrips':
      return state.score.trips > bonus.trips;
  }
}

/** Everything the goal asks for, done: checked once the run is won. */
function bonusDone(state: GameState, bonus: BonusGoal): boolean {
  switch (bonus.kind) {
    case 'noneLost':
    case 'withinTrips':
      return true;
    case 'couriersOnTime':
      return bonusSubjects(state, bonus).every(onTime);
    case 'allVips':
      return bonusSubjects(state, bonus).every((p) => p.state === 'delivered');
  }
}
